import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Location } from 'history';
import { SHOW_TASK_GROUPING_NAME, SHOW_TASK_ORDER_NAME } from '../../global/constants';
import { PROJECT_ANALYTICS } from '../../global/analyticsConstants';
import { updateTaskFilterGrouping, updateTaskFilterOrder } from '../../helpers/common';
import { rootStore } from '../../mobx/rootStore';
import { ROUTES } from '../../routes/urls';
import { trackAnalyticActivity } from '../../services/analyticsService';
import { dispatchAppError } from '../../services/appService';
import { captureException } from '../../services/logService';
import { updateProjectData } from '../../services/projectServices';
import { AccountingProps } from '../../interfaces/ProjectInterface';
import { useAccountingDisplayProperties } from './useAccountingDisplayProperties';
import { useAccountingFileFilters } from './useAccountingFileFilters';
import { useAccountingProjectLoader } from './useAccountingProjectLoader';
import { useAccountingTaskFilters } from './useAccountingTaskFilters';

export type AccountingData = ReturnType<typeof useAccountingData>;

export const useAccountingData = (props: AccountingProps) => {
  const [loading, setLoading] = useState(false);
  const [isOrderDropdownOpen, setIsOrderDropdownOpen] = useState(false);
  const [isOpenGroupMenu, setIsOpenGroupMenu] = useState(false);
  const [isOpenCompletedTaskDropdown, setIsOpenCompletedTaskDropdown] = useState(false);
  const [isFileOrderDropdownOpen, setIsFileOrderDropdownOpen] = useState(false);
  const { projectStore, workspaceStore, taskStore, currentUser } = rootStore;
  const { currentProject, createProjectInput, filesViewListType } = projectStore;
  const { taskFilterType: taskFilter } = currentProject || {};
  const { currentWorkspace } = workspaceStore;
  const history = useHistory();
  const params: { id: string } = useParams();
  const location: Location = useLocation();
  const setFilesViewListType = useCallback(
    (listType: number) => projectStore.setFilesViewListType(listType),
    [projectStore]
  );

  const loadData = useAccountingProjectLoader({
    currentProject,
    currentWorkspace,
    history,
    params,
    props,
    setFilesViewListType
  });

  useEffect(() => {
    loadData();
  }, [loadData]);

  const view = useMemo(() => {
    const basePath = `/projects/details/${params.id}`;
    return {
      isTask: location.pathname === `${basePath}/tasks`,
      isFile: location.pathname === `${basePath}/files`,
      isMilestone: location.pathname === `${basePath}/milestone`,
      isMessages: location.pathname === `${basePath}/messages`,
      isOverview: location.pathname === `${basePath}/overview`
    };
  }, [location.pathname, params.id]);

  const archiveProject = useCallback(async () => {
    try {
      setLoading(true);
      if (!currentWorkspace?.isAdmin) return dispatchAppError(`You can't archive project.`);
      if (await updateProjectData(params.id, { isArchived: true })) {
        projectStore.removeProjects([params.id]);
        trackAnalyticActivity(PROJECT_ANALYTICS.DELETED);
        history.push(ROUTES.ALL_PROJECT);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.isAdmin, history, params.id, projectStore]);

  const taskFilterActions = useAccountingTaskFilters({
    currentProject,
    onCloseCompletedDropdown: () => setIsOpenCompletedTaskDropdown(false),
    onCloseGroupMenu: () => setIsOpenGroupMenu(false),
    onCloseOrderDropdown: () => setIsOrderDropdownOpen(false),
    params,
    props,
    taskFilter
  });
  const fileFilterActions = useAccountingFileFilters({
    currentProject,
    onCloseFileOrderDropdown: () => setIsFileOrderDropdownOpen(false),
    params
  });
  const displayPropertyActions = useAccountingDisplayProperties({ currentProject, taskFilter });

  return {
    archiveProject,
    currentProject,
    currentUser,
    currentWorkspace,
    createProjectInput,
    filesViewListType,
    getDisplayProperties: displayPropertyActions.getDisplayProperties,
    getGroupName: () => (currentProject ? SHOW_TASK_GROUPING_NAME[updateTaskFilterGrouping(currentProject) || 1] : ''),
    getOrderName: () => (currentProject ? SHOW_TASK_ORDER_NAME[updateTaskFilterOrder(currentProject)] : ''),
    handleDisplayPropertyToggle: displayPropertyActions.handleDisplayPropertyToggle,
    history,
    isTaskFilterOpen: taskStore.isTaskFilterOpen,
    loadData,
    loading,
    menus: {
      isFileOrderDropdownOpen,
      isOpenCompletedTaskDropdown,
      isOpenGroupMenu,
      isOrderDropdownOpen,
      setIsFileOrderDropdownOpen,
      setIsOpenCompletedTaskDropdown,
      setIsOpenGroupMenu,
      setIsOrderDropdownOpen
    },
    ...fileFilterActions,
    ...taskFilterActions,
    params,
    taskFilter,
    view
  };
};
